import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import * as authApi from '@/api/auth';
import { ApiError } from '@/api/http';
import type { CurrentUser, UserRole } from '@/types/contracts';

export const useAuthStore = defineStore('auth', () => {
  const user = ref<CurrentUser | null>(null);
  const isInitialized = ref(false);
  const isLoading = ref(false);
  const sessionCheckError = ref('');
  const isCheckingSession = ref(false);
  let initialization: Promise<void> | null = null;

  const isAuthenticated = computed(() => user.value !== null);

  async function initialize(): Promise<void> {
    if (isInitialized.value) return;
    if (initialization) return initialization;
    isCheckingSession.value = true;
    initialization = authApi.getCurrentUser().then((currentUser) => {
      user.value = currentUser;
      isInitialized.value = true;
      sessionCheckError.value = '';
    }).catch((error: unknown) => {
      if (error instanceof ApiError && (error.status === 401 || error.code === 'AUTH_SESSION_EXPIRED')) {
        user.value = null;
        isInitialized.value = true;
        sessionCheckError.value = '';
      } else {
        // 重启、断网和网关错误不代表会话过期，保留原 Cookie 并允许重新校验。
        sessionCheckError.value = '服务暂时无法连接，恢复后将自动校验登录状态';
      }
    }).finally(() => {
      isCheckingSession.value = false;
      initialization = null;
    });
    return initialization;
  }

  async function login(credentials: authApi.LoginRequest): Promise<void> {
    isLoading.value = true;
    try {
      user.value = await authApi.login(credentials);
      isInitialized.value = true;
      sessionCheckError.value = '';
    } finally {
      isLoading.value = false;
    }
  }

  async function logout(): Promise<void> {
    try {
      await authApi.logout();
    } finally {
      user.value = null;
      isInitialized.value = true;
    }
  }

  function hasRole(roles?: UserRole[]): boolean {
    return !roles?.length || Boolean(user.value && roles.includes(user.value.role));
  }

  return { user, isInitialized, isLoading, sessionCheckError, isCheckingSession,
    isAuthenticated, initialize, login, logout, hasRole };
});
